import React from 'react';
import { Link } from 'react-router-dom';
import {
  Stars,
  CashCoin,
  GraphUpArrow,
  Award,
  Grid1x2,
  CheckCircleFill,
} from 'react-bootstrap-icons';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import { useLocale } from '../i18n/LocaleContext';
import heroImage from '../assets/creator-hero.png';
import './Home.scss';

export default function Home() {
  const { t } = useLocale();

  // "How it works" — the 5 steps.
  const steps = ['1', '2', '3', '4', '5'];

  // "More than a collaboration" — feature cards.
  const features = [
    { key: 'paid', Icon: CashCoin },
    { key: 'perf', Icon: GraphUpArrow },
    { key: 'circle', Icon: Award },
    { key: 'place', Icon: Grid1x2 },
  ];

  // "Who can apply" — checklist items.
  const who = ['1', '2', '3', '4'];

  return (
    <div className="home">
      <Navbar />

      {/* ============ HERO (dark, image-backed) ============ */}
      <section
        className="hero hero--dark"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="hero__accent" aria-hidden="true" />
        <div className="hero__scrim" aria-hidden="true" />
        <div className="container-x hero__inner">
          <Reveal as="span" className="hero__badge" delayIndex={0}>
            <Stars size={13} />
            {t('hero.badge')}
          </Reveal>
          <Reveal as="h1" className="hero__title" delayIndex={1}>
            {t('hero.title')}
          </Reveal>
          <Reveal as="p" className="hero__tagline" delayIndex={2}>
            {t('hero.tagline')}
          </Reveal>
          <Reveal as="p" className="hero__subtitle" delayIndex={3}>
            {t('hero.subtitle')}
          </Reveal>
          <Reveal className="hero__cta" delayIndex={4}>
            <Link to="/login" className="btn-o btn-o--brand">
              {t('hero.ctaLogin')}
            </Link>
            <a href="#how" className="btn-o btn-o--ghost">
              {t('hero.ctaLearn')}
            </a>
          </Reveal>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section id="how" className="section section--tint">
        <div className="container-x">
          <div className="section-head">
            <Reveal as="span" className="eyebrow">
              {t('how.eyebrow')}
            </Reveal>
            <Reveal as="h2" className="section-title" delayIndex={1}>
              {t('how.title')}
            </Reveal>
          </div>
          <div className="steps">
            {steps.map((n, i) => (
              <Reveal as="article" className="step" key={n} delayIndex={i}>
                <span className="step__num">{n}</span>
                <div className="step__text">
                  <h3 className="step__title">{t(`how.${n}.title`)}</h3>
                  <p className="step__body">{t(`how.${n}.body`)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ MORE THAN A COLLABORATION ============ */}
      <section id="more" className="section">
        <div className="container-x">
          <div className="section-head">
            <Reveal as="span" className="eyebrow">
              {t('more.eyebrow')}
            </Reveal>
            <Reveal as="h2" className="what__title" delayIndex={1}>
              {t('more.title')}
            </Reveal>
          </div>
          <div className="feature-grid feature-grid--2">
            {features.map(({ key, Icon }, i) => (
              <Reveal as="article" className="feature-card" key={key} delayIndex={i}>
                <span className="feature-card__icon">
                  <Icon size={22} />
                </span>
                <h3 className="feature-card__title">{t(`more.${key}.title`)}</h3>
                <p className="feature-card__body">{t(`more.${key}.body`)}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHO CAN APPLY ============ */}
      <section id="who" className="section section--tint">
        <div className="container-x">
          <div className="section-head">
            <Reveal as="span" className="eyebrow">
              {t('who.eyebrow')}
            </Reveal>
            <Reveal as="h2" className="section-title" delayIndex={1}>
              {t('who.title')}
            </Reveal>
          </div>
          <div className="who-list">
            {who.map((n, i) => (
              <Reveal className="who-line" key={n} delayIndex={i}>
                <CheckCircleFill size={18} className="who-line__icon" />
                <span>{t(`who.${n}`)}</span>
              </Reveal>
            ))}
          </div>
          <Reveal className="who-highlight" delayIndex={who.length}>
            <Stars size={18} className="who-highlight__icon" />
            <span>{t('who.highlight')}</span>
          </Reveal>
          <Reveal as="p" className="who-note" delayIndex={who.length + 1}>
            {t('who.note')}
          </Reveal>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section id="cta" className="section cta">
        <div className="container-x">
          <Reveal className="cta__card">
            <div className="cta__glow" aria-hidden="true" />
            <h2 className="cta__title">{t('cta.title')}</h2>
            <p className="cta__body">{t('cta.body')}</p>
            <div className="cta__actions">
              <Link to="/login" className="btn-o btn-o--light">
                {t('cta.apply')}
              </Link>
            </div>
            <p className="cta__note">{t('cta.note')}</p>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
